import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getUIThemeConfig } from '@/lib/ui-theme-config';

/** Cast `as typeof Button` at the end preserves polymorphic `component` prop (e.g. `component="a"` with `href`). */
const CustomButton = styled(Button)(({ theme }) => {
  const uiConfig = getUIThemeConfig(theme);

  return {
    // Only apply custom background for contained buttons
    '&.MuiButton-contained': {
      backgroundColor: uiConfig.buttons.contained.backgroundColor,
      color: uiConfig.buttons.contained.color,
      fontWeight: 600,
      padding: '8px 16px',
      '&:hover': {
        backgroundColor: uiConfig.buttons.contained.hoverBackgroundColor,
        opacity: 0.9,
      },
      '&:disabled': {
        backgroundColor: uiConfig.buttons.contained.disabledBackgroundColor,
        color: uiConfig.buttons.contained.disabledColor,
        opacity: 0.6,
      },
    },
    // Outlined buttons match brand book Secondary: bg-canvas, text-primary, line-strong border
    '&.MuiButton-outlined': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
      borderWidth: '1px',
      fontWeight: 600,
      padding: '8px 16px',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
      '&:disabled': {
        color: uiConfig.buttons.outlined.disabledColor,
        borderColor: uiConfig.buttons.outlined.disabledBorderColor,
      },
    },
    // Text buttons should use vibrant colors
    '&.MuiButton-text': {
      color: uiConfig.buttons.text.color,
      fontWeight: 600, // Make text bolder
      padding: '8px 12px', // Consistent padding
      '&:hover': {
        backgroundColor: uiConfig.buttons.text.hoverBackgroundColor,
        color: uiConfig.buttons.text.hoverColor,
        boxShadow: theme.shadows[1], // Subtle shadow
        transform: 'translateY(-1px)', // Slight lift effect
      },
      '&:disabled': {
        color: uiConfig.buttons.text.disabledColor,
      },
    },

    // Error variants
    '&.MuiButton-containedError': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
        opacity: 0.9,
      },
      '&:disabled': {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
        opacity: 0.6,
      },
    },
    '&.MuiButton-outlinedError': {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.main,
      borderWidth: '1px',
      fontWeight: 600,
      padding: '8px 16px',
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.error.dark,
        borderColor: theme.palette.error.dark,
        opacity: 0.8,
      },
      '&:disabled': {
        color: theme.palette.error.light,
        borderColor: theme.palette.error.light,
      },
    },
    '&.MuiButton-textError': {
      color: theme.palette.error.main,
      fontWeight: 600,
      padding: '8px 12px',
      '&:hover': {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
        boxShadow: theme.shadows[1],
        transform: 'translateY(-1px)',
      },
      '&:disabled': {
        color: theme.palette.error.light,
      },
    },
  };
}) as typeof Button;

export default CustomButton;

